import { get, isEqual } from "lodash";
import { useMemo } from "react";

import type { ClientWithConfig } from "@platform/dmv-api";

type StatusBadgeProps = {
  isModel: boolean;
  isPlatformClient?: boolean;
  selectedPath: string;
  differs: boolean;
};

type ValueCellProps = {
  value: unknown;
};

type ComparisonStatusProperties = {
  label: string;
  className: string; // Additional classes to apply to the status badge
};


type ComparisonRow = ClientWithConfig & {
  value: unknown;
  differs: boolean;
};

export type ComparisonTableProps = {
  clients: ClientWithConfig[];
  selectedPath: string;
  modelClientId: string | null;
  onSetModel: (_clientId: string) => void;
  selectedIds: Set<string>;
  onToggleSelect: (_clientId: string) => void;
  onSelectAllDiffering: () => void;
  onClearSelection: () => void;
  onInspect?: (_client: ClientWithConfig) => void;
};

/**
 * Status badge shown in the rightmost column of each row.
 *
 * MODEL is the client that is being used as the comparison point.
 * Priority order:
 *   1. Model + platform client  → PLATFORM (purple, solid)
 *   2. Model only               → MODEL (blue, solid)
 *   3. Non-model platform client → platform (purple, muted)
 *   4. No path selected         → nothing
 *   5. Value differs from model → ≠ differs (orange)
 *   6. Value matches model      → = matches (green)
 */
const StatusBadge = ({
  isModel,
  isPlatformClient,
  selectedPath,
  differs,
}: StatusBadgeProps) => {


  const ComparisonStatus = ({ label, className }: ComparisonStatusProperties) => (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );

  if (isModel && isPlatformClient) {
    return <ComparisonStatus label="PLATFORM" className="bg-purple-100 text-purple-700" />;
  }
  if (isModel) {
    return <ComparisonStatus label="MODEL" className="bg-blue-100 text-blue-700" />;
  }
  if (isPlatformClient) {
    return <ComparisonStatus label="platform" className="bg-purple-50 text-purple-500" />;
  }
  if (!selectedPath) return null;
  if (differs) {
      return <ComparisonStatus label="≠ differs" className="bg-orange-100 text-orange-700" />;
  }
  return <ComparisonStatus label="= matches" className="bg-green-100 text-green-700" />;
};



/**
 * Renders the value at a given path as a compact display string.
 * Primitives are shown inline; objects/arrays are JSON-stringified and truncated.
 */
const ValueCell = ({ value }: ValueCellProps) => {
  if (value === undefined) {
    return <span className="text-gray-300 italic text-xs">undefined</span>;
  }
  if (value === null) {
    return <span className="text-gray-400 italic text-xs">null</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span
        className={`font-mono text-xs font-medium ${
          value ? "text-green-600" : "text-red-500"
        }`}
      >
        {String(value)}
      </span>
    );
  }
  if (typeof value === "number") {
    return (
      <span className="font-mono text-xs text-blue-600">{String(value)}</span>
    );
  }
  if (typeof value === "string") {
    return (
      <span className="font-mono text-xs text-gray-700 truncate max-w-xs block">
        &quot;{value}&quot;
      </span>
    );
  }

  // Object or array — show truncated JSON
  const json = JSON.stringify(value, null, 0);
  const display = json.length > 80 ? json.slice(0, 80) + "…" : json;
  return (
    <span
      title={JSON.stringify(value, null, 2)}
      className="font-mono text-xs text-gray-600 truncate max-w-xs block cursor-help"
    >
      {display}
    </span>
  );
};


/**
 * Comparison table for destination clients.
 *
 * Shows every destination client in a scrollable list with:
 * - Checkbox for selection
 * - Display name + client ID
 * - The value at `selectedPath` extracted from each client's config
 * - A status badge: MODEL / matches / differs
 */
const ComparisonTable = ({
  clients,
  selectedPath,
  modelClientId,
  onSetModel,
  selectedIds,
  onToggleSelect,
  onSelectAllDiffering,
  onClearSelection,
  onInspect,
}: ComparisonTableProps) => {
  const modelClient = useMemo(
    () => clients.find((c) => c.id === modelClientId),
    [clients, modelClientId]
  );

  const modelValue = useMemo(
    () =>
      modelClient && selectedPath
        ? get(modelClient.config, selectedPath)
        : undefined,
    [modelClient, selectedPath]
  );

  // Enrich each client with its current value and diff status
  const rows = useMemo((): ComparisonRow[] => {
    if (!selectedPath) {
      return clients.map((c) => ({ ...c, value: undefined, differs: false }));
    }

    return clients.map((c) => {
      const value = get(c.config, selectedPath);
      const differs = !isEqual(value, modelValue);
      return { ...c, value, differs };
    });
  }, [clients, selectedPath, modelValue]);

  const differingCount = rows.filter(
    (r) => r.id !== modelClientId && r.differs
  ).length;

  const destCount = clients.filter((c) => !c.isPlatformClient).length;

  if (!clients || clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No destination clients found.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Table toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50 shrink-0">
        <div className="flex items-center gap-x-3">
          <span className="text-sm font-medium text-gray-700">
            {destCount} destination client{destCount !== 1 ? "s" : ""}
          </span>
          {selectedPath && differingCount > 0 && (
            <span className="text-xs text-orange-600">
              {differingCount} differ from model
            </span>
          )}
        </div>
        <div className="flex items-center gap-x-2">
          {selectedIds.size > 0 && (
            <button
              onClick={onClearSelection}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear ({selectedIds.size})
            </button>
          )}
          {differingCount > 0 && (
            <button
              onClick={onSelectAllDiffering}
              className="text-xs px-2.5 py-1 border border-orange-300 text-orange-700 rounded hover:bg-orange-50 transition-colors"
            >
              Select all differing ({differingCount})
            </button>
          )}
        </div>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-[28px_1fr_1fr_90px_40px] gap-x-3 px-4 py-1.5 border-b border-gray-100 bg-white shrink-0">
        <div />
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Client
        </div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {selectedPath ? selectedPath : "Select a config path"}
        </div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Status
        </div>
        <div />
      </div>

      {/* Scrollable rows */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
        {rows.map((client) => {
          const isModel = client.id === modelClientId;
          const isChecked = selectedIds.has(client.id);
          const differs = !isModel && client.differs;

          return (
            <div
              key={client.id}
              className={`grid grid-cols-[28px_1fr_1fr_90px_40px] gap-x-3 px-4 py-2 items-start transition-colors ${
                isModel
                  ? "bg-blue-50"
                  : isChecked
                  ? "bg-orange-50"
                  : "hover:bg-gray-50"
              }`}
            >
              {/* Checkbox */}
              <div className="pt-0.5">
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isModel}
                  onChange={() => onToggleSelect(client.id)}
                  className="w-4 h-4 accent-black cursor-pointer"
                />
              </div>

              {/* Client name + ID */}
              <div className="min-w-0">
                <button
                  onClick={() => onSetModel(client.id)}
                  className="text-sm font-medium text-gray-800 hover:text-black hover:underline text-left truncate max-w-full block"
                  title="Click to set as model"
                >
                  {client.displayName}
                </button>
                <span className="text-xs text-gray-400 font-mono">
                  {client.id}
                </span>
              </div>

              {/* Value */}
              <div className="min-w-0 pt-0.5">
                {selectedPath ? (
                  <ValueCell value={client.value} />
                ) : (
                  <span className="text-xs text-gray-300">&mdash;</span>
                )}
              </div>

              {/* Status badge */}
              <div className="pt-0.5">
                <StatusBadge
                  isModel={isModel}
                  isPlatformClient={client.isPlatformClient}
                  selectedPath={selectedPath}
                  differs={differs}
                />
              </div>

              {/* Inspect button */}
              <div className="pt-0.5">
                {onInspect && (
                  <button
                    onClick={() => onInspect(client)}
                    className="text-gray-400 hover:text-black text-xs"
                    title="View full config"
                  >
                    &#x1f50d;
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComparisonTable;
